import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  Stack,
  Text,
  VStack,
  useColorModeValue,
  Divider,
  SimpleGrid,
  Icon,
} from '@chakra-ui/react';
import { useAuthenticationStatus, useUserDefaultRole } from '@nhost/react';
import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import FullScreenSpinner from 'src/components/Spinner';
import { FaUtensils, FaMotorcycle, FaMobileAlt, FaHeart } from 'react-icons/fa';

const LandingPage = () => {
  // All hooks must be called before any conditional returns
  const { isAuthenticated, isLoading } = useAuthenticationStatus();
  const userDefaultRole = useUserDefaultRole();
  const bgColor = useColorModeValue('white', 'dark.800');
  const textColor = useColorModeValue('dark.800', 'white');
  const featuresBg = useColorModeValue('gray.50', 'dark.900');
  const footerBg = useColorModeValue('gray.100', 'dark.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const brandPink = 'brand.500';
  const _brandOrange = 'brand.600';

  if (isLoading) {
    return <FullScreenSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to={userDefaultRole === 'admin' ? '/admin' : '/order'} replace={true} />;
  }

  return (
    <Box minH="100vh" bgColor={bgColor} position="relative">
      {/* Hero Section */}
      <Box
        bgImage="linear-gradient(to right, rgba(255, 26, 61, 0.85), rgba(255, 94, 0, 0.85)), url('/assets/img/food.jpg')"
        bgSize="cover"
        bgPosition="center"
        height="90vh"
        position="relative"
        boxShadow="xl"
        overflow="hidden"
      >
        {/* Decorative food icons floating in background */}
        <Box position="absolute" width="full" height="full" zIndex="1" pointerEvents="none">
          {/* These will be decorative food emojis that float in the background */}
          <Text
            position="absolute"
            top="10%"
            left="5%"
            fontSize="6xl"
            opacity="0.2"
            animation="float 8s ease-in-out infinite"
          >
            🍕
          </Text>
          <Text
            position="absolute"
            top="20%"
            right="10%"
            fontSize="5xl"
            opacity="0.2"
            animation="float 10s ease-in-out infinite"
          >
            🍔
          </Text>
          <Text
            position="absolute"
            bottom="15%"
            left="15%"
            fontSize="5xl"
            opacity="0.2"
            animation="float 7s ease-in-out infinite"
          >
            🍜
          </Text>
          <Text
            position="absolute"
            bottom="30%"
            right="20%"
            fontSize="6xl"
            opacity="0.2"
            animation="float 12s ease-in-out infinite"
          >
            🍣
          </Text>
          <Text
            position="absolute"
            top="40%"
            left="25%"
            fontSize="4xl"
            opacity="0.2"
            animation="float 9s ease-in-out infinite"
          >
            🌮
          </Text>
        </Box>

        <style>{`
          @keyframes float {
            0% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-20px) rotate(5deg);
            }
            100% {
              transform: translateY(0px) rotate(0deg);
            }
          }
        `}</style>
        <Container maxW="container.xl" position="relative" zIndex="2" height="100%">
          <Flex
            height="100%"
            direction="column"
            justifyContent="center"
            alignItems="center"
            textAlign="center"
            color="white"
          >
            <Box width="200px" height="200px" mb={6} position="relative">
              <Image
                src="/assets/img/magauu-logo.png"
                alt="Magauu Logo"
                width="100%"
                height="100%"
                objectFit="contain"
                filter="drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.3))"
              />
            </Box>

            <Heading
              as="h1"
              fontSize={['4xl', '5xl', '6xl']}
              fontWeight="extrabold"
              maxW="container.md"
              mb={6}
              letterSpacing="tight"
              lineHeight="shorter"
            >
              Delicious Food,{' '}
              <Box as="span" bgGradient="linear(to-r, yellow.300, orange.300)" bgClip="text">
                Delivered Fast
              </Box>
            </Heading>

            <Text fontSize={['lg', 'xl', '2xl']} maxW="container.md" mb={10} fontWeight="medium" lineHeight="tall">
              Your favorite meals from the best local restaurants delivered to your doorstep in minutes. Enjoy hot,
              fresh food without leaving your home!
            </Text>

            <Stack direction={['column', 'row']} spacing={6}>
              <Button
                as={Link}
                to="/sign-in"
                size="lg"
                bgGradient="linear(to-r, brand.500, brand.600)"
                _hover={{ bgGradient: 'linear(to-r, brand.600, brand.500)', transform: 'translateY(-2px)' }}
                color="white"
                fontWeight="bold"
                px={10}
                py={7}
                fontSize="xl"
                boxShadow="xl"
                _active={{ transform: 'translateY(0)' }}
                transition="all 0.2s"
              >
                Order Now
              </Button>
              <Button
                as={Link}
                to="#how-it-works"
                size="lg"
                bg="whiteAlpha.900"
                color="brand.500"
                fontWeight="bold"
                px={10}
                py={7}
                fontSize="xl"
                boxShadow="xl"
                _hover={{ bg: 'white', transform: 'translateY(-2px)' }}
                _active={{ transform: 'translateY(0)' }}
                transition="all 0.2s"
              >
                Learn More
              </Button>
            </Stack>
          </Flex>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box py={24} bg={bgColor} id="how-it-works" position="relative" overflow="hidden">
        {/* Decorative elements */}
        <Box
          position="absolute"
          top="-50px"
          right="-50px"
          width="200px"
          height="200px"
          borderRadius="full"
          bg="brand.50"
          opacity="0.4"
          zIndex="0"
        />
        <Box
          position="absolute"
          bottom="-30px"
          left="-30px"
          width="150px"
          height="150px"
          borderRadius="full"
          bg="orange.50"
          opacity="0.4"
          zIndex="0"
        />
        <Container maxW="container.xl">
          <VStack spacing={20}>
            <Box textAlign="center">
              <Text
                color="brand.500"
                fontWeight="bold"
                fontSize="lg"
                textTransform="uppercase"
                letterSpacing="wide"
                mb={3}
              >
                Simple & Easy Process
              </Text>
              <Heading
                textAlign="center"
                mb={4}
                color={textColor}
                fontSize={['3xl', '4xl', '5xl']}
                fontWeight="extrabold"
                lineHeight="shorter"
              >
                How Magauu Works
              </Heading>
              <Text
                fontSize={['md', 'lg', 'xl']}
                color={useColorModeValue('gray.600', 'gray.400')}
                maxW="container.md"
                mx="auto"
              >
                Getting your favorite food delivered has never been easier
              </Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} w="full">
              <ProcessCard
                icon={FaUtensils}
                title="Browse & Order"
                description="Explore local restaurants, view menus, and order your favorite dishes with just a few taps"
                step="1"
              />
              <ProcessCard
                icon={FaMobileAlt}
                title="Real-time Tracking"
                description="Watch your order's journey from restaurant kitchen to your doorstep with live updates"
                step="2"
              />
              <ProcessCard
                icon={FaMotorcycle}
                title="Quick Delivery"
                description="Our delivery partners use optimized routes to ensure your food arrives hot, fresh and fast"
                step="3"
              />
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Features Section */}
      <Box py={24} bg={featuresBg} position="relative" overflow="hidden">
        {/* Decorative patterns */}
        <Box
          position="absolute"
          top="0"
          right="0"
          bottom="0"
          left="0"
          opacity="0.03"
          backgroundImage="url('data:image/svg+xml,%3Csvg width%3D%2220%22 height%3D%2220%22 xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath d%3D%22M0 0h20v20H0z%22 fill%3D%22%23FF1A3D%22 fill-opacity%3D%220.4%22%2F%3E%3C%2Fsvg%3E')"
          backgroundSize="30px 30px"
          zIndex="0"
        />
        <Container maxW="container.xl">
          <VStack spacing={16}>
            <Box textAlign="center">
              <Text
                color="brand.500"
                fontWeight="bold"
                fontSize="lg"
                textTransform="uppercase"
                letterSpacing="wide"
                mb={3}
              >
                Benefits You&apos;ll Love
              </Text>
              <Heading
                textAlign="center"
                mb={4}
                color={textColor}
                fontSize={['3xl', '4xl', '5xl']}
                fontWeight="extrabold"
                lineHeight="shorter"
              >
                Why Choose Magauu
              </Heading>
              <Text
                fontSize={['md', 'lg', 'xl']}
                color={useColorModeValue('gray.600', 'gray.400')}
                maxW="container.md"
                mx="auto"
              >
                We&apos;re not just another food delivery app - we&apos;re your culinary companion
              </Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} w="full">
              <FeatureCard
                title="Diverse Cuisine Selection"
                description="From local favorites to international delights, discover a world of flavors from hundreds of restaurants in your area"
                icon="🍕"
              />
              <FeatureCard
                title="Lightning-Fast Delivery"
                description="Our optimized delivery network and dedicated drivers ensure your food arrives hot and fresh in record time - average delivery in under 30 minutes!"
                icon="⚡"
              />
              <FeatureCard
                title="Exclusive Rewards"
                description="Enjoy special promotions, loyalty rewards, and personalized offers from your favorite local restaurants. Earn points with every order!"
                icon="🎁"
              />
            </SimpleGrid>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} w="full">
              <FeatureCard
                title="Easy Payment Options"
                description="Multiple secure payment methods including credit cards, digital wallets, and cash on delivery. Pay how you want, every time!"
                icon="💳"
              />
              <FeatureCard
                title="Contactless Delivery"
                description="Safe, hygienic, and contactless delivery options available for all your orders. Your safety is our priority!"
                icon="👐"
              />
              <FeatureCard
                title="24/7 Customer Support"
                description="Our friendly support team is always ready to assist you with any questions or concerns. We're just a message away!"
                icon="🛎️"
              />
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* CTA Section with Food Images */}
      <Box py={24} bgGradient="linear(to-r, brand.500, brand.600)" color="white" position="relative" overflow="hidden">
        {/* Food pattern in background */}
        <Box
          position="absolute"
          top="0"
          right="0"
          bottom="0"
          left="0"
          opacity="0.1"
          backgroundImage="url('/assets/img/food-pattern.svg')"
          backgroundSize="200px"
          backgroundRepeat="repeat"
          zIndex="0"
        />
        {/* Decorative elements */}
        <Box
          position="absolute"
          top="-10%"
          left="-5%"
          width="300px"
          height="300px"
          borderRadius="full"
          bg="whiteAlpha.100"
          zIndex="1"
        />
        <Box
          position="absolute"
          bottom="-15%"
          right="-10%"
          width="400px"
          height="400px"
          borderRadius="full"
          bg="whiteAlpha.100"
          zIndex="1"
        />

        <Container maxW="container.xl" textAlign="center" position="relative" zIndex="2">
          <Heading
            mb={6}
            fontSize={['3xl', '4xl', '5xl']}
            fontWeight="extrabold"
            lineHeight="shorter"
            textShadow="0 2px 10px rgba(0,0,0,0.1)"
          >
            Hungry? We&apos;ve Got You Covered
          </Heading>
          <Text fontSize={['lg', 'xl', '2xl']} mb={10} maxW="container.lg" mx="auto" fontWeight="medium">
            Join thousands of food lovers who trust Magauu for their daily meals. Fresh food, fast delivery, fantastic
            experience! Order from your favorite local restaurants with just a few taps.
          </Text>
          <Stack direction={['column', 'row']} spacing={6} justify="center">
            <Button
              as={Link}
              to="/sign-in"
              size="lg"
              bg="white"
              color="brand.500"
              _hover={{ bg: 'white', transform: 'translateY(-2px)', boxShadow: 'xl' }}
              px={10}
              py={7}
              fontSize="xl"
              fontWeight="bold"
              boxShadow="lg"
              transition="all 0.2s"
            >
              Order Your First Meal
            </Button>
            <Button
              as={Link}
              to="#"
              size="lg"
              variant="outline"
              borderWidth="2px"
              borderColor="white"
              color="white"
              _hover={{ bg: 'whiteAlpha.200', transform: 'translateY(-2px)', boxShadow: 'xl' }}
              px={10}
              py={7}
              fontSize="xl"
              fontWeight="bold"
              transition="all 0.2s"
            >
              Download Our App
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Footer */}
      <Box py={16} bg={footerBg}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={10} mb={10}>
            <Box>
              <Flex direction="column" alignItems={{ base: 'center', md: 'flex-start' }}>
                <Box width="100px" height="100px" position="relative" mb={6}>
                  <Image
                    src="/assets/img/magauu-logo.png"
                    alt="Magauu Logo"
                    width="100%"
                    height="100%"
                    objectFit="contain"
                  />
                </Box>

                <Text color={textColor} mb={6} textAlign={{ base: 'center', md: 'left' }}>
                  Bringing delicious meals from your favorite local restaurants straight to your doorstep since 2023.
                  Serving over 50+ cities nationwide!
                </Text>
              </Flex>
            </Box>

            <Box>
              <Heading as="h4" size="md" mb={6} color={textColor} textAlign={{ base: 'center', md: 'left' }}>
                Explore
              </Heading>
              <VStack spacing={3} align={{ base: 'center', md: 'flex-start' }}>
                <Link to="#">
                  <Text color={textColor} _hover={{ color: 'brand.500' }}>
                    Restaurants
                  </Text>
                </Link>
                <Link to="#">
                  <Text color={textColor} _hover={{ color: 'brand.500' }}>
                    Cuisines
                  </Text>
                </Link>
                <Link to="#">
                  <Text color={textColor} _hover={{ color: 'brand.500' }}>
                    Popular Dishes
                  </Text>
                </Link>
                <Link to="#">
                  <Text color={textColor} _hover={{ color: 'brand.500' }}>
                    Special Offers
                  </Text>
                </Link>
              </VStack>
            </Box>

            <Box>
              <Heading as="h4" size="md" mb={6} color={textColor} textAlign={{ base: 'center', md: 'left' }}>
                Company
              </Heading>
              <VStack spacing={3} align={{ base: 'center', md: 'flex-start' }}>
                <Link to="#">
                  <Text color={textColor} _hover={{ color: 'brand.500' }}>
                    About Us
                  </Text>
                </Link>
                <Link to="#">
                  <Text color={textColor} _hover={{ color: 'brand.500' }}>
                    Partner With Us
                  </Text>
                </Link>
                <Link to="#">
                  <Text color={textColor} _hover={{ color: 'brand.500' }}>
                    Careers
                  </Text>
                </Link>
                <Link to="#">
                  <Text color={textColor} _hover={{ color: 'brand.500' }}>
                    Blog
                  </Text>
                </Link>
              </VStack>
            </Box>

            <Box>
              <Heading as="h4" size="md" mb={6} color={textColor} textAlign={{ base: 'center', md: 'left' }}>
                Legal
              </Heading>
              <VStack spacing={3} align={{ base: 'center', md: 'flex-start' }}>
                <Link to="/terms-and-conditions">
                  <Text color={textColor} _hover={{ color: 'brand.500' }}>
                    Terms of Service
                  </Text>
                </Link>
                <Link to="/privacy-policy">
                  <Text color={textColor} _hover={{ color: 'brand.500' }}>
                    Privacy Policy
                  </Text>
                </Link>
                <Link to="/sign-in">
                  <Text color={textColor} _hover={{ color: 'brand.500' }}>
                    Sign In
                  </Text>
                </Link>
                <Link to="#">
                  <Text color={textColor} _hover={{ color: 'brand.500' }}>
                    Contact Us
                  </Text>
                </Link>
              </VStack>
            </Box>
          </SimpleGrid>

          <Divider borderColor={borderColor} opacity={0.6} my={8} />

          <Flex direction={{ base: 'column', md: 'row' }} justifyContent="space-between" alignItems="center">
            <Text color={textColor} mb={{ base: 4, md: 0 }}>
              &copy; {new Date().getFullYear()} Magauu. All rights reserved.
            </Text>

            <Flex gap={6}>
              <Link to="#">
                <Icon as={FaHeart} color={brandPink} boxSize={5} />
              </Link>
              <Link to="#">
                <Icon as={FaUtensils} color={textColor} boxSize={5} />
              </Link>
              <Link to="#">
                <Icon as={FaMotorcycle} color={textColor} boxSize={5} />
              </Link>
              <Link to="#">
                <Icon as={FaMobileAlt} color={textColor} boxSize={5} />
              </Link>
            </Flex>
          </Flex>
        </Container>
      </Box>
    </Box>
  );
};

const FeatureCard = ({ title, description, icon }) => {
  const bgColor = useColorModeValue('white', 'dark.700');
  const textColor = useColorModeValue('dark.800', 'white');
  const descriptionColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const iconBg = useColorModeValue('brand.50', 'whiteAlpha.200');

  return (
    <Box
      p={8}
      borderRadius="xl"
      boxShadow="md"
      bg={bgColor}
      border="1px"
      borderColor={borderColor}
      flex="1"
      textAlign="center"
      transition="all 0.3s ease"
      _hover={{
        transform: 'translateY(-8px)',
        boxShadow: 'xl',
        borderColor: 'brand.500',
      }}
    >
      <Flex
        w="70px"
        h="70px"
        borderRadius="full"
        bg={iconBg}
        color="brand.500"
        fontSize="3xl"
        align="center"
        justify="center"
        mb={6}
        mx="auto"
      >
        {icon}
      </Flex>
      <Heading as="h3" size="lg" mb={4} color={textColor} fontWeight="bold">
        {title}
      </Heading>
      <Text color={descriptionColor} fontSize="md" lineHeight="tall">
        {description}
      </Text>
    </Box>
  );
};

const ProcessCard = ({ icon, title, description, step }) => {
  const bgColor = useColorModeValue('white', 'dark.700');
  const textColor = useColorModeValue('dark.800', 'white');
  const descriptionColor = useColorModeValue('gray.600', 'gray.300');
  const accentColor = useColorModeValue('brand.500', 'brand.400');
  const cardBg = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  return (
    <Box
      p={8}
      borderRadius="xl"
      bg={cardBg}
      border="1px"
      borderColor={borderColor}
      boxShadow="md"
      transition="all 0.3s ease"
      _hover={{
        transform: 'translateY(-5px)',
        boxShadow: 'xl',
        borderColor: 'brand.500',
      }}
    >
      <VStack spacing={6} align="center">
        <Flex position="relative" justifyContent="center" alignItems="center">
          <Box
            position="absolute"
            top="-10px"
            right="-10px"
            bg={accentColor}
            color="white"
            borderRadius="full"
            width="40px"
            height="40px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontWeight="bold"
            fontSize="xl"
            boxShadow="lg"
            border="2px solid white"
            zIndex="2"
          >
            {step}
          </Box>
          <Flex
            width="120px"
            height="120px"
            borderRadius="full"
            bg={bgColor}
            boxShadow="lg"
            justifyContent="center"
            alignItems="center"
            color={accentColor}
            border="1px"
            borderColor={borderColor}
          >
            <Icon as={icon} fontSize="4xl" />
          </Flex>
        </Flex>
        <Heading as="h3" size="lg" color={textColor} fontWeight="bold">
          {title}
        </Heading>
        <Text color={descriptionColor} textAlign="center" fontSize="md" lineHeight="tall">
          {description}
        </Text>
      </VStack>
    </Box>
  );
};

export default LandingPage;
